import React, { useEffect, useState } from 'react'
import { Button } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import { useHistory, useLocation } from 'react-router-dom'
import queryString from 'query-string'
import AuthService from 'tools/AuthService'
import strings from 'strings'
import { useTasks } from './hooks/useTasks'
import { useTaskCounts } from './hooks/useTaskCounts'
import TaskFilters from './TaskFilters'
import TaskList from './TaskList'
import NewTaskModal from './modals/NewTaskModal'
import TaskDetailDrawer from './TaskDetailDrawer'
import './CollaborativeTasks.scss'

/**
 * Top-level component for the Tasks tab on the application detail page.
 *
 * Composes:
 *   - Header (title + counts summary + + NEW TASK button)
 *   - Filter chips (Open / All / Completed)
 *   - List of task rows
 *   - New Task modal (controlled here)
 *   - Detail drawer (also controlled here)
 *
 * The `highlightTaskId` prop opens the drawer on mount — used by the bell
 * dropdown deep-link behaviour.
 */
const TasksTab = ({ applicationUuid, applicationRef, highlightTaskId }) => {
    const t = strings.CT
    const history = useHistory()
    const location = useLocation()
    const { filter, setFilter, tasks, loading, refetch } = useTasks(applicationUuid, 'open')
    const { counts, refetch: refetchCounts } = useTaskCounts(applicationUuid)

    const [newOpen, setNewOpen] = useState(false)
    const [openTask, setOpenTask] = useState(null) // the task object whose drawer is open
    // Read-only users may view tasks but not create them (consistent with other
    // application-detail write controls).
    const isReadOnly = AuthService.isReadOnlyUser && AuthService.isReadOnlyUser()

    useEffect(() => {
        if (highlightTaskId) {
            setOpenTask({ id: highlightTaskId })
        }
    }, [highlightTaskId])

    const handleAnyChange = () => {
        refetch()
        refetchCounts()
    }

    const handleDrawerClose = () => {
        setOpenTask(null)
        const params = queryString.parse(location.search)
        if (params.task) {
            delete params.task
            const next = queryString.stringify(params)
            history.replace(`${location.pathname}${next ? `?${next}` : ''}`)
        }
    }

    return (
        <div className="ct-tab-root">
            <div className="ct-header">
                <div>
                    <div className="ct-header__title">{t.HEADING}</div>
                    <div className="ct-header__summary">
                        {t.SUMMARY
                            .replace('{open}', counts.open)
                            .replace('{completed}', counts.completed)
                            .replace('{external}', counts.withExternalUser)}
                    </div>
                </div>
                <div className="ct-header__actions">
                    {!isReadOnly && (
                        <Button
                            variant="contained"
                            className="ct-new-task-btn"
                            startIcon={<AddIcon />}
                            onClick={() => setNewOpen(true)}
                        >
                            {strings.CT.NEW_TASK.replace('+ ', '')}
                        </Button>
                    )}
                </div>
            </div>

            <TaskFilters filter={filter} setFilter={setFilter} counts={counts} />

            <TaskList
                tasks={tasks}
                loading={loading}
                filter={filter}
                onOpen={(task) => setOpenTask(task)}
            />

            <NewTaskModal
                open={newOpen}
                onClose={() => setNewOpen(false)}
                applicationUuid={applicationUuid}
                applicationRef={applicationRef}
                onCreated={handleAnyChange}
            />

            <TaskDetailDrawer
                open={!!openTask}
                onClose={handleDrawerClose}
                taskId={openTask?.id}
                applicationUuid={applicationUuid}
                onAnyChange={handleAnyChange}
            />
        </div>
    )
}

export default TasksTab
